import { Component, OnInit, OnDestroy } from "@angular/core";
import { ExperiencesService } from "@services/experience/experiences.service";
import {
  Experience,
  ExperienceCountService,
  Operation,
} from "@services/experience/experience-count.service";
import { ErrorEmitterService } from "@services/emitters/error-emitter.service";
import { ReplaySubject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Router } from "@angular/router";
import * as FileSaver from "file-saver";
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";

@Component({
  selector: "app-experience-listing",
  templateUrl: "./experience-listing.component.html",
  styleUrls: ["./experience-listing.component.scss"],
})
export class ExperienceListingComponent implements OnDestroy, OnInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public experiences: Array<any>;
  public currentRoute: string;
  public currentExperience: Experience;
  public title: string;
  public noRecordWording: string;
  public loader: boolean = false;
  public loaderMore: boolean = false;
  public hideLoader: boolean = false;
  public noRecords: boolean;
  public loadingMoreEmpty: boolean = false;
  public skip: number = 10;
  public take: number = 10;
  public confirmationMessage: string = "ConfirmDelete";
  public menuExp = {
    edit: true,
    bin: true,
    binDraft: false,
    delete: true,
    editPublished: false,
    editRejected: false,
    restore: false,
    qr: true,
  };
  constructor(
    private experienceSvc: ExperiencesService,
    private experienceCounterSvc: ExperienceCountService,
    private router: Router,
    private errorEmitter: ErrorEmitterService,
    private translate: TranslateService
  ) {
    translate.onLangChange.subscribe((lang) => {
      this.getExperienceByStatus(this.currentRoute);
    });
    if (this.router.url.indexOf("drafts") > -1) {
      this.currentRoute = "draft";
      this.currentExperience = Experience.DRAFT;
      this.title = "Draft experiences";
      this.noRecordWording = "There are no drafted experiences.";
      this.menuExp.delete = false;
      this.menuExp.bin = false;
      this.menuExp.binDraft = true;
    }
    if (this.router.url.indexOf("pendingApproval") > -1) {
      this.currentRoute = "pendingApproval";
      this.currentExperience = Experience.PENDING_APPROVAL;
      this.title = "Pending approval";
      this.noRecordWording = "There are no experiences waiting approval.";
      this.menuExp.delete = false;
      this.menuExp.bin = false;
      this.menuExp.binDraft = true;
    }
    if (this.router.url.indexOf("bin") > -1) {
      this.currentRoute = "canceled";
      this.title = "Binned experiences";
      this.menuExp.bin = false;
      this.menuExp.restore = true;
      this.menuExp.qr = false;
      this.noRecordWording = "There are no binned experiences.";
      this.menuExp.edit = false;
    }
    if (this.router.url.indexOf("rejected") > -1) {
      this.currentRoute = "rejected";
      this.currentExperience = Experience.REJECTED;
      this.title = "Rejected experiences";
      this.noRecordWording = "There are no rejected experiences.";
      this.menuExp.bin = false;
      this.menuExp.edit = false;
      this.menuExp.editRejected = true;
    }
    if (this.router.url.indexOf("active") > -1) {
      this.currentRoute = "active";
      this.currentExperience = Experience.ACTIVE;
      this.title = "Active experiences";
      this.noRecordWording = "There are no active experiences.";
      this.menuExp.delete = false;
      this.menuExp.edit = false;
      this.menuExp.editPublished = true;
    }

    if (this.router.url.indexOf("expired") > -1) {
      this.currentRoute = "expired";
      this.currentExperience = Experience.EXPIRED;
      this.title = "Expired experiences";
      this.noRecordWording = "There are no expired experiences.";
      this.menuExp.delete = false;
      this.menuExp.edit = false;
      this.menuExp.editPublished = true;
    }
    this.getExperienceByStatus(this.currentRoute);
  }

  ngOnInit() {
    this.getExperienceCount();
  }

  public getExperienceByStatus(route) {
    this.loadingMoreEmpty = false;
    this.skip = 10;
    this.experienceSvc
      .getExperienceByStatus(route)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          this.experiences = x.result;
          if (this.experiences.length == 0) {
            this.noRecords = true;
            this.hideLoader = false;
          }
          if (this.experiences.length < 10) {
            this.hideLoader = false;
          } else {
            this.hideLoader = true;
          }
          for (let i = 0, len = this.experiences.length; i < len; i++) {
            if (!this.experiences[i].title) {
              this.experiences[i].title = "Untitled";
            } else {
              this.experiences[i].title = this.experiences[i].title;
            }
            if (this.experiences[i].types.length > 0) {
              let typeIndex = this.experiences[i].types.findIndex(
                (item) => item === "guided_tours"
              );
              if (typeIndex > -1) {
                this.experiences[i].types[typeIndex] = "guided tours";
              }
            }
          }
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public getMoreExperiences() {
    this.loaderMore = true;
    this.experienceSvc
      .getMoreExperiences(this.currentRoute, this.skip, this.take)
      .subscribe(
        (x) => {
          let moreExp = x.result;
          if (moreExp.length == 0) {
            this.loadingMoreEmpty = true;
          }

          for (let i = 0, len = moreExp.length; i < len; i++) {
            let versionIndex = moreExp[i].versionInfos.findIndex(
              (item) => item.languageTag === "en"
            );
            if (!moreExp[i].title) {
              moreExp[i].title = "Untitled";
              this.experiences.push(moreExp[i]);
            } else {
              moreExp[i].title = moreExp[i].title;
              this.experiences.push(moreExp[i]);
            }

            if (moreExp[i].types.length > 0) {
              let typeIndex = moreExp[i].types.findIndex(
                (item) => item === "guided_tours"
              );
              if (typeIndex > -1) {
                moreExp[i].types[typeIndex] = "guided tours";
              }
            }
          }

          this.skip += 10;
          this.loaderMore = false;
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public edit(exp) {
    this.router.navigate(["create-experience", exp.id]);
  }

  public editPublished(exp) {
    this.experienceSvc
      .editPublished(exp.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          if (!x.result) {
            return false;
          } else {
            this.router.navigate(["create-experience", exp.id]);
          }
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public editRejected(exp) {
    this.experienceSvc
      .editRejected(exp.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          if (!x.result) {
            return false;
          } else {
            this.router.navigate(["create-experience", exp.id]);
          }
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public binExperience(exp, i) {
    this.experienceSvc
      .binExperience(exp.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          this.experiences.splice(i, 1);
          this.experienceCounterSvc.changeCount(
            this.currentExperience,
            Operation.DECREASING
          );
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public binExperienceCancel(exp, i) {
    this.experienceSvc
      .binExperienceCancel(exp.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          this.experiences.splice(i, 1);
          this.experienceCounterSvc.changeCount(
            this.currentExperience,
            Operation.DECREASING
          );
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public restore(exp, i) {
    this.experienceSvc
      .restore(exp.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          this.experiences.splice(i, 1);
          this.experienceCounterSvc.changeCount(
            Experience.DRAFT,
            Operation.INCREASING
          );
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public deleteExperience(exp, i) {
    if (window.confirm(this.translate.instant(this.confirmationMessage))) {
      this.experienceSvc
        .deleteExperience(exp.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          (x) => {
            this.experiences.splice(i, 1);
            this.experienceCounterSvc.changeCount(
              this.currentExperience,
              Operation.DECREASING
            );
          },
          (err) => {
            this.errorEmitter.sendError(true);
          }
        );
    }
  }

  public getExperienceCount() {
    this.experienceSvc
      .getExperienceCount()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          const count = x;
          this.experienceCounterSvc.setCount(count);
          this.experienceCounterSvc.sendCount(count);
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  public getQr(exp) {
    this.experienceSvc
      .getQr(exp.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          FileSaver.saveAs(x, "QR code");
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
